import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';

@Component({
  selector: 'app-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = '';
  @Input() name: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() class: string = '';
  @Input() id: string = '';
  @Input() color: 'primary' | 'accent' | 'warn' = 'primary';
  @Input() labelPosition: 'before' | 'after' = 'after';
  @Input() indeterminate: boolean = false;
  @Input() description: string = '';
  @Input() formControl: FormControl | null = null;

  value: boolean = false;
  touched: boolean = false;

  // ControlValueAccessor implementation
  onChange = (value: boolean) => {};
  onTouched = () => {};

  writeValue(value: boolean): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onToggle(): void {
    if (!this.disabled) {
      this.value = !this.value;
      this.touched = true;

      // Update form control if connected
      if (this.formControl) {
        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
        this.formControl.setValue(this.value);
      }

      this.onChange(this.value);
      this.onTouched();
    }
  }

  onBlur(): void {
    this.touched = true;
    this.onTouched();
  }

  onLabelClick(): void {
    if (!this.disabled) {
      this.onToggle();
    }
  }

  onCheckboxChange(event: any): void {
    if (!this.disabled) {
      this.value = event.checked;
      this.touched = true;

      // Update form control if connected
      if (this.formControl) {
        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
        this.formControl.setValue(this.value);
      }

      this.onChange(this.value);
      this.onTouched();
    }
  }

  ngOnInit(): void {
    // Component initialization if needed
  }

  hasError(): boolean {
    if (this.formControl) {
      // Pattern 1: Check for requiredTrue validator errors
      if (this.formControl.errors && this.formControl.errors['requiredTrue']) {
        return this.formControl.dirty || this.formControl.touched;
      }

      // Pattern 2: Check if required but value is false
      if (this.required && this.value === false && (this.formControl.dirty || this.formControl.touched)) {
        return true;
      }

      // Pattern 3: Check for general validation errors
      if (this.formControl.invalid && (this.formControl.dirty || this.formControl.touched)) {
        return true;
      }

      // Pattern 4: Check if form control is touched and has no value
      if (this.formControl.touched && this.formControl.value === false && this.required) {
        return true;
      }
    }

    // Standalone mode: if required but unchecked
    return this.required && this.value === false && this.touched;
  }
  getErrorMessage(): string {
    if (this.formControl && this.formControl.errors) {
      if (this.formControl.errors['requiredTrue']) {
        return 'You must accept the terms and conditions';
      }
      if (this.formControl.errors['required']) {
        return 'This field is required';
      }
    }
    // Standalone mode fallback
    if (this.required && this.value === false) {
      return 'This field is required';
    }

    return '';
  }
}
